import { useContext, createContext, useState } from 'react';

import { UserInfo, UserInfoData, UserInfoProps } from 'etc/types';
import { useUserData } from 'hooks/useUserData';
import { useReporterInfo } from 'hooks/useReporterInfo';
import { useMarketInfo } from 'context/MarketInfo/MarketInfoProvider';

export const UserInfoContext = createContext<UserInfo>({} as UserInfo);
export const useUserInfo = () => useContext(UserInfoContext);

export const withUserInfo = <P extends UserInfoProps>(Comp: React.ComponentType<P>) => {
  return (props: Omit<P, keyof UserInfoProps>) => (
    <UserInfoContext.Consumer>{userInfo => <Comp {...(props as P)} userInfo={userInfo} />}</UserInfoContext.Consumer>
  );
};

const UserInfoProvider: React.FC = ({ children }) => {
  const [userProvidedEmail, setUserProvidedEmail] = useState<string>();
  const { isLoading, error, userData } = useUserData();
  const { emailType, country, error: useReporterError } = useReporterInfo(userData?.id, userProvidedEmail);

  const { market } = useMarketInfo();

  const data: UserInfoData = {
    username: userData?.id,
    displayName: userData?.display_name,
    email: userData?.email,
    emailType: emailType,
    country: country || (market?.split('-').shift() as string),
    avatar: userData?.images?.[0] && userData.images[0].url,
    followerCount: userData?.followers ? userData.followers.total : 0,
    product: userData?.product as UserInfoData['product'],
  };

  return (
    <UserInfoContext.Provider
      value={{
        loading: isLoading,
        error: !!error,
        isRateLimited: !!useReporterError,
        data,
        userCountryIsoCode: data.country,
        setUserProvidedEmail,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export default UserInfoProvider;
